/**
 * PRNG determinista: mulberry32.
 *
 * OJO. Este fichero tiene un gemelo en `backend/app/services/prng.py` y las dos
 * implementaciones TIENEN que dar exactamente la misma secuencia para el mismo
 * seed. El servidor usa la suya para recalcular cuantas monedas eran posibles en
 * una partida; si se desvian un solo bit, el antifraude acusa de trampa a
 * jugadores honestos.
 *
 * Si tocas esto, toca el gemelo y corre `backend/tests/test_prng_paridad.py`.
 *
 * Por que mulberry32 y no Math.random(): Math.random() no acepta semilla, asi
 * que el servidor no podria reproducir el nivel. Y por que no un Mersenne:
 * mulberry32 son ocho lineas, se reimplementa sin errores en cualquier lenguaje
 * y para generar obstaculos su calidad sobra.
 */

export type Rng = () => number;

/** Devuelve un generador de flotantes en [0, 1). */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return function next(): number {
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Entero en [min, max] ambos incluidos. */
export function rngInt(rng: Rng, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/** Elige un elemento de la lista. La lista no puede estar vacia. */
export function rngPick<T>(rng: Rng, items: readonly T[]): T {
  const i = Math.floor(rng() * items.length);
  // noUncheckedIndexedAccess obliga a esto; y ademas protege del caso rng()===1
  return items[Math.min(i, items.length - 1)] as T;
}

/** true con probabilidad p (0..1). */
export function rngChance(rng: Rng, p: number): boolean {
  return rng() < p;
}

/**
 * Convierte un seed de texto (el que manda el servidor) en el entero de 32 bits
 * que necesita mulberry32. FNV-1a, que tambien esta replicado en el backend.
 */
export function seedFromString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
